import dayjs from 'dayjs';
import { produce } from 'immer';

import { ConstraintOperator } from '@/common/ConstraintsEditor/Constraint/facetMap';
import { getAllDomains } from '@/common/domains';
import { detectValueItemType } from '@/common/valueItemType';
import {
  Constraint,
  ConstraintType,
  GetExperimentDto,
} from '@/features/experiment/api';
import {
  ExperimentAssignmentStrategy,
  getDomain,
  getExperimentStartAndEndTime,
  isRestrictByConstraint,
} from '@/features/experiment/constants';
import {
  Choice,
  Experiment,
  GetExperiment,
  RegionalExperimentConfigValues,
  Variable,
} from '@/features/experiment/domain';
import Service from '@/lib/App';

export function getExperimentDtoToExperiment(
  dto: GetExperimentDto,
): GetExperiment {
  /**
   * If the experiment domain has the TIME_SLICE_V2 strategy, and the experiment strategy is TIME_SLICE,
   * then set the experiment strategy to TIME_SLICE_V2.
   */
  const experiment: Experiment = {
    name: dto?.experiment?.name,
    variables: dto?.experiment?.variables,
    constraints: dto?.experiment?.constraints,
    state: dto?.experiment?.state,
    domain: dto?.experiment?.domain,
    slotting: dto?.experiment?.slotting,
    metadata: {
      primaryMetricOptOutEnabled: false,
      primaryMetricOptOutReason: '',
      ...(dto?.experiment?.metadata || {}),
    },
    createdAt: dto?.experiment?.createdAt,
    approvalFlow: dto?.experiment?.approvalFlow,
    autoAdaptive: dto?.experiment?.autoAdaptive,
    schedulingDefinition: dto?.experiment?.schedulingDefinition,
    assignBy: dto?.experiment?.assignBy,
    targetBy: dto?.experiment?.targetBy,
    regionalExperimentName: dto?.experiment?.regionalExperimentName,
    owner: dto?.experiment?.['owner'],
  };

  const allDomainMetadata = Service?.cache?.getAllDomainMetadata();
  const experimentDomainMetadata = allDomainMetadata?.find(
    (metadata) => metadata.name === dto.experiment?.domain,
  );

  const sourceConfigurations = dto?.regional_experiment_config_values || [
    {
      variables: experiment?.variables || [],
      country_code: '',
    },
  ];
  const experimentStrategy =
    sourceConfigurations?.[0]?.variables?.[0]?.strategy;

  const configurations: RegionalExperimentConfigValues[] = produce(
    sourceConfigurations,
    (draft) => {
      draft.forEach((configuration: RegionalExperimentConfigValues) => {
        configuration.variables?.forEach((variable: Variable) => {
          /**
           * If the experiment domain has the TIME_SLICE_V2 strategy, and the experiment strategy is TIME_SLICE,
           * then set the variable strategy to TIME_SLICE_V2.
           */
          if (
            experimentDomainMetadata?.strategies?.includes(
              ExperimentAssignmentStrategy.TIME_SLICE_V2,
            ) &&
            experimentStrategy === ExperimentAssignmentStrategy.TIME_SLICE
          )
            variable.strategy = ExperimentAssignmentStrategy.TIME_SLICE_V2;

          /**
           * If the experiment strategy is WEIGHTED_CHOICE, then set the choice chance to the choice chance * 100.
           */
          if (
            experimentStrategy === ExperimentAssignmentStrategy.WEIGHTED_CHOICE
          ) {
            variable.choices.forEach((choice: Choice) => {
              choice.chance = choice.chance * 100;
            });
          }

          if (
            experimentStrategy === ExperimentAssignmentStrategy.UNIFORM_CHOICE
          ) {
            variable.choices.forEach((choice: Choice) => {
              choice.chance = 100 / variable.choices.length;
            });
          }

          variable?.choices?.forEach((c: Choice) => {
            if (c?.group) {
              const value = c.group;
              const target = value
                ? value.substring(0, value.indexOf('.'))
                : getDomain(experiment?.domain)?.targetBy || '';

              const constraint: Constraint = {
                op: ConstraintOperator.EQUALS.value,
                value: value,
                target: target,
              };

              constraint.valueItemType = detectValueItemType(constraint);
              c.groupConstraint = constraint;
            }
          });
        });
      });
    },
  );

  /**
   * If the experiment has start and end time constraints, set the experiment start and end time.
   */
  const [unixStart, unixEnd] = getExperimentStartAndEndTime(experiment);

  const nextExperiment = produce(experiment, (draft) => {
    draft.startTime = unixStart ? dayjs.unix(unixStart) : null;
    draft.endTime = unixEnd ? dayjs.unix(unixEnd) : null;
    draft.sampleSize = (experiment?.slotting?.byPercentage || 0) * 100;
    draft.duration = draft?.variables?.[0]?.choices?.[0]?.span;

    if (experimentStrategy === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE) {
      draft.dayLength = Math.round(
        dayjs(draft.endTime).diff(dayjs(draft.startTime), 'minutes') / 60 / 24,
      );
    }

    if (draft?.slotting?.byGroup) {
      draft.byGroup = {
        op: ConstraintOperator.EQUALS.value,
        value: draft.slotting.byGroup,
        target: getAllDomains().find((domain) => domain.name === draft.domain)
          ?.targetBy,
      };

      draft.byGroup.valueItemType = detectValueItemType(draft.byGroup);
    }

    const transformedConstraints = draft?.constraints?.map((constraint) => {
      if (
        !constraint.valueItemType &&
        (constraint.type == ConstraintType.Standard || !constraint.type)
      ) {
        return {
          ...constraint,
          valueItemType: detectValueItemType(constraint),
        };
      }
      return constraint;
    });

    // we do the following in case the experiment is not created from the portal
    // and because UI always assume the first constraint will be the restrict by constraint
    // filter out restrict by constraint. technically there is only 1
    const restrictByConstraints = transformedConstraints.filter(
      (constraint) => {
        return isRestrictByConstraint(constraint);
      },
    );

    const restOfConstraints = transformedConstraints.filter((constraint) => {
      return !isRestrictByConstraint(constraint);
    });

    draft.constraints = [...restrictByConstraints, ...restOfConstraints];
  });

  return {
    experiment: nextExperiment,
    regional_experiment_config_values: configurations,
  };
}
